package nl.appstuff.padelchecker.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import nl.appstuff.padelchecker.site.SiteColors
import nl.appstuff.padelchecker.site.WebPlatform
import nl.appstuff.padelchecker.site.localization.LocalSiteLanguage
import nl.appstuff.padelchecker.site.localization.LocalSiteLanguageController
import nl.appstuff.padelchecker.site.localization.LocalSiteStrings
import nl.appstuff.padelchecker.site.localization.SiteLanguage
import nl.appstuff.padelchecker.site.platform
import org.jetbrains.compose.web.css.*

@Composable
fun NavBar() {
    val breakpoint = rememberBreakpoint()
    val isDesktop = breakpoint >= Breakpoint.MD
    val strings = LocalSiteStrings.current

    Box(
        modifier = Modifier
            .position(Position.Sticky)
            .fillMaxWidth()
            .background(SiteColors.grey1),
        contentAlignment = Alignment.Center,
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .maxWidth(1120.px)
                .padding(leftRight = 24.px, topBottom = 14.px),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            NavBrandLogo()

            val downloadUrl = when (platform()) {
                WebPlatform.ANDROID -> "https://play.google.com/store/apps/details?id=nl.appstuff.padelchecker"
                WebPlatform.IOS -> "https://apps.apple.com/nl/app/padelchecker-nl-padelbanen/id6759914204"
                else -> null
            }
            if (isDesktop) {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(20.px),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    NavLink("#faq", strings.navFaq)
                    LanguageToggle()
                    PillLink(
                        text = if (downloadUrl != null) strings.navDownload else strings.navFindCourt,
                        path = downloadUrl ?: "/zoeken",
                    )
                }
            } else {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(10.px),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    LanguageToggle()
                    PillLink(
                        text = if (downloadUrl != null) strings.navDownload else strings.navFindCourt,
                        path = downloadUrl ?: "/zoeken",
                        compact = true,
                    )
                }
            }
        }
    }
}

@Composable
private fun LanguageToggle() {
    val language = LocalSiteLanguage.current
    val controller = LocalSiteLanguageController.current
    val strings = LocalSiteStrings.current
    var expanded by remember { mutableStateOf(false) }

    Box(
        modifier = Modifier
            .position(Position.Relative),
    ) {
        Box(
            modifier = Modifier
                .size(36.px)
                .borderRadius(999.px)
                .background(SiteColors.blueLight)
                .onClick { expanded = !expanded },
            contentAlignment = Alignment.Center,
        ) {
            Image(
                src = if (language == SiteLanguage.NL) "/images/flags/flag_nl.svg"
                else "/images/flags/flag_gb.svg",
                alt = strings.navLanguageEnglish,
                modifier = Modifier
                    .width(20.px)
                    .height(14.px),
            )
        }
        if (expanded) {
            Box(
                modifier = Modifier
                    .position(Position.Absolute)
                    .top(42.px)
                    .right(0.px)
                    .width(76.px)
                    .zIndex(10)
                    .background(SiteColors.white)
                    .border(1.px, LineStyle.Solid, SiteColors.blueLight)
                    .borderRadius(10.px)
                    .padding(6.px),
            ) {
                Column(modifier = Modifier.fillMaxWidth()) {
                    LanguageMenuItem(
                        code = strings.navLanguageDutch,
                        flag = "/images/flags/flag_nl.svg",
                        onClick = {
                            controller.set(SiteLanguage.NL)
                            expanded = false
                        },
                    )
                    LanguageMenuItem(
                        code = strings.navLanguageEnglish,
                        flag = "/images/flags/flag_gb.svg",
                        onClick = {
                            controller.set(SiteLanguage.EN)
                            expanded = false
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun LanguageMenuItem(
    code: String,
    flag: String,
    onClick: () -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .whiteSpace(WhiteSpace.NoWrap)
            .padding(leftRight = 8.px, topBottom = 6.px)
            .cursor(Cursor.Pointer)
            .onClick { onClick() },
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.px),
    ) {
        Image(
            src = flag,
            alt = code,
            modifier = Modifier.width(20.px).height(14.px),
        )
        SpanText(
            code,
            modifier = Modifier
                .fontSize(12.px)
                .fontWeight(FontWeight.Medium)
                .color(SiteColors.blue),
        )
    }
}

@Composable
private fun NavLink(path: String, label: String) {
    Link(
        path = path,
        variant = UncoloredLinkVariant,
        modifier = Modifier
            .color(SiteColors.textLight)
            .fontSize(16.px)
            .fontWeight(FontWeight.Medium)
            .textDecorationLine(TextDecorationLine.None),
    ) {
        SpanText(label)
    }
}

@Composable
private fun NavBrandLogo() {
    BrandLogo(
        lightVariant = false,
        modifier = Modifier.width(148.px),
    )
}

@Composable
private fun PillLink(text: String, path: String, compact: Boolean = false) {
    Link(
        path = path,
        variant = UncoloredLinkVariant,
        modifier = Modifier
            .padding(leftRight = if (compact) 16.px else 24.px, topBottom = if (compact) 8.px else 10.px)
            .background(SiteColors.blue)
            .borderRadius(999.px)
            .fontSize(if (compact) 14.px else 16.px)
            .fontWeight(FontWeight.Medium)
            .color(SiteColors.white)
            .textDecorationLine(TextDecorationLine.None),
    ) {
        SpanText(text)
    }
}
